import type { TrackPoint } from '../trips.api';
import type { TripStop } from './trip-stops.policy';

/** `[west, south, east, north]`. */
export type TrackBounds = [number, number, number, number];

const SOURCES: TrackPoint['source'][] = ['gps', 'phone'];

/** One line per source, so the tracker's route and the driver's phone's can be told apart; a source with fewer than two fixes draws none. */
function trackLines(points: TrackPoint[]): GeoJSON.Feature[] {
  return SOURCES.flatMap((source) => {
    const fixes = points.filter((point) => point.source === source);
    if (fixes.length < 2) return [];
    return [
      {
        type: 'Feature' as const,
        properties: { kind: 'track', source },
        geometry: { type: 'LineString' as const, coordinates: fixes.map((point) => [point.lng, point.lat]) },
      },
    ];
  });
}

/** The plan's stops that have coordinates, as numbered markers. */
export function stopFeatures(stops: Pick<TripStop, 'position' | 'name' | 'lat' | 'lng'>[]): GeoJSON.Feature[] {
  return stops.flatMap((stop) =>
    stop.lat === null || stop.lng === null
      ? []
      : [{ type: 'Feature' as const, properties: { kind: 'stop', name: stop.name }, geometry: { type: 'Point' as const, coordinates: [stop.lng, stop.lat] } }],
  );
}

/** The fix nearest in time to a moment (a trip event's device time), so an event can be shown where the truck was. */
export function pointNearestTime(points: TrackPoint[], isoTime: string): TrackPoint | null {
  const target = new Date(isoTime).getTime();
  let nearest: TrackPoint | null = null;
  let nearestGap = Infinity;
  for (const point of points) {
    const gap = Math.abs(new Date(point.deviceTime).getTime() - target);
    if (gap < nearestGap) {
      nearest = point;
      nearestGap = gap;
    }
  }
  return nearest;
}

/** The route so far, the plan's stops, and the newest fix as the truck's marker. */
export function trackFeatures(points: TrackPoint[], stops: Pick<TripStop, 'position' | 'name' | 'lat' | 'lng'>[] = []): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [...trackLines(points), ...stopFeatures(stops)];
  const latest = points.at(-1);
  if (latest) {
    features.push({
      type: 'Feature',
      properties: { kind: 'truck', source: latest.source, hasHeading: latest.headingDeg !== null, heading: latest.headingDeg ?? 0 },
      geometry: { type: 'Point', coordinates: [latest.lng, latest.lat] },
    });
  }
  return { type: 'FeatureCollection', features };
}

/** The box round every fix, for framing the map once; null when there is nothing to frame. */
export function trackBounds(points: TrackPoint[]): TrackBounds | null {
  if (points.length === 0) return null;
  const lngs = points.map((point) => point.lng);
  const lats = points.map((point) => point.lat);
  return [Math.min(...lngs), Math.min(...lats), Math.max(...lngs), Math.max(...lats)];
}
